import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Play, ChevronRight, Dumbbell, Trophy, MessageCircle, UtensilsCrossed, Moon } from "lucide-react";
import api from "../lib/api";
import { ProgressRing, Loader, Pill } from "../components/common";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/customer/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);

  if (!data) return <Loader />;
  const { coach, today_workout, upcoming, recent, completion_rate, streak, unread_messages, subscription_status, goals } = data;

  return (
    <div className="p-5 fade-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-secondary text-sm">Welcome back,</p>
          <h1 className="font-heading text-4xl leading-none mt-1">{user.name.split(" ")[0]}</h1>
        </div>
        <div className="flex items-center gap-1.5 bg-brand/15 px-3 py-1.5 rounded-full">
          <Flame className="w-4 h-4 text-brand" />
          <span className="font-num font-semibold text-brand">{streak}</span>
          <span className="text-xs text-secondary">day streak</span>
        </div>
      </div>

      {coach && (
        <div className="flex items-center gap-3 mt-5 card-surface rounded-xl p-3" data-testid="coach-card">
          <div className="w-11 h-11 rounded-full overflow-hidden bg-brand/20 flex items-center justify-center">
            {coach.picture ? <img src={coach.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-brand">{coach.name[0]}</span>}
          </div>
          <div className="flex-1">
            <p className="text-xs text-secondary">Your Coach</p>
            <p className="font-semibold">{coach.name}</p>
          </div>
          <button data-testid="dash-message-coach" onClick={() => navigate("/app/messages")} className="relative p-2.5 rounded-lg bg-white/5">
            <MessageCircle className="w-5 h-5" />
            {unread_messages > 0 && <span className="absolute -top-1 -right-1 bg-brand text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">{unread_messages}</span>}
          </button>
        </div>
      )}

      <div className="mt-5 rounded-2xl p-5 bg-gradient-to-br from-brand/20 to-surface border border-brand/30" data-testid="today-workout-card">
        <div className="flex items-center justify-between">
          <span className="label-xs">Today's Workout</span>
          {today_workout && <span className="text-xs text-secondary">{today_workout.difficulty}</span>}
        </div>
        {today_workout && !today_workout.is_rest ? (
          <>
            <h2 className="font-heading text-3xl mt-2">{today_workout.workout_name}</h2>
            <p className="text-secondary text-sm mt-1">{today_workout.exercises?.length} exercises · {today_workout.duration_min} min · {today_workout.difficulty}</p>
            {today_workout.status === "completed" ? (
              <div className="mt-4 flex items-center gap-2 text-success font-semibold"><Trophy className="w-5 h-5" /> Completed today — great work!</div>
            ) : (
              <button data-testid="start-workout-btn" onClick={() => navigate(`/app/workout/${today_workout.id}`)}
                className="w-full mt-4 py-4 bg-brand text-white font-bold uppercase tracking-wide rounded-xl hover:bg-brand-hover transition-all glow-red flex items-center justify-center gap-2">
                <Play className="w-5 h-5 fill-white" /> Start Today's Workout
              </button>
            )}
          </>
        ) : (
          <>
            <h2 className="font-heading text-2xl mt-2 text-secondary flex items-center gap-2"><Moon className="w-6 h-6" /> Rest Day</h2>
            <p className="text-secondary text-sm mt-1">{today_workout?.is_rest ? "Recovery day scheduled by your coach — rest up and recharge." : "No workout scheduled. Recover well — you've earned it."}</p>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mt-5">
        <div className="card-surface rounded-xl p-4 flex flex-col items-center justify-center" data-testid="completion-ring">
          <ProgressRing value={completion_rate} size={104} sub="Completion" />
        </div>
        <div className="card-surface rounded-xl p-4 flex flex-col justify-center gap-3">
          <div>
            <p className="label-xs">This week</p>
            <p className="font-heading text-3xl text-brand">{data.weekly_completed}</p>
            <p className="text-xs text-secondary">workouts done</p>
          </div>
          {goals?.[0] && (
            <div>
              <p className="label-xs">{goals[0].title}</p>
              <div className="h-1.5 bg-white/10 rounded-full mt-1.5"><div className="h-full bg-success rounded-full" style={{ width: `${goals[0].progress}%` }} /></div>
            </div>
          )}
        </div>
      </div>

      <button data-testid="dash-nutrition" onClick={() => navigate("/app/nutrition")}
        className="w-full mt-5 card-surface rounded-xl p-4 flex items-center gap-3 hover:border-brand transition-all text-left">
        <div className="w-10 h-10 rounded-lg bg-success/15 flex items-center justify-center"><UtensilsCrossed className="w-5 h-5 text-success" /></div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Nutrition Plan</p>
          <p className="text-xs text-secondary">View meals & macros from your coach</p>
        </div>
        <ChevronRight className="w-4 h-4 text-tertiary" />
      </button>

      {upcoming?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg">Upcoming</h3>
          <div className="space-y-2 mt-3">
            {upcoming.map((w) => (
              <div key={w.id} className="card-surface rounded-xl p-3 flex items-center gap-3" data-testid="upcoming-item">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">{w.is_rest ? <Moon className="w-5 h-5 text-tertiary" /> : <Dumbbell className="w-5 h-5 text-brand" />}</div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{w.workout_name}</p>
                  <p className="text-xs text-secondary">{w.scheduled_date}{!w.is_rest && ` · ${w.duration_min} min`}</p>
                </div>
                {!w.is_rest && <ChevronRight className="w-4 h-4 text-tertiary" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {recent?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg">Recently Completed</h3>
          <div className="space-y-2 mt-3">
            {recent.map((w) => (
              <div key={w.id} className="card-surface rounded-xl p-3 flex items-center gap-3 opacity-80" data-testid="recent-item">
                <div className="w-10 h-10 rounded-lg bg-success/15 flex items-center justify-center"><Trophy className="w-5 h-5 text-success" /></div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{w.workout_name}</p>
                  <p className="text-xs text-secondary">{w.scheduled_date}</p>
                </div>
                {w.flagged && <Pill tone="warning">Pain flagged</Pill>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
